/**
 * Export handlers for the exit screens (G2.4, G2 test *d*).
 *
 * One per data route. Each returns the CURRENT view — G2.4's fidelity rule is that an
 * export reflects the filters, sort, column set and drill level the planner is looking at,
 * not the unfiltered underlying query — and each carries all nine provenance fields, which
 * the type makes non-optional (G2 test *e*).
 *
 * **Every sheet in this feature now leaves against the pinned client.** The five that read
 * from a worked example are gone: each re-reads the SAME operation its screen reads, over
 * the same perimeter, through `collectPages`. That is what makes G2.4's fidelity rule true
 * here — none of these surfaces applies a filter, so the query the export runs IS the view
 * the planner is looking at — and it is why no resolver declares `illustrative` any more.
 * The tenth provenance field is absent rather than empty, which is what made its presence
 * mean something while it was there.
 *
 * Numbers leave as `raw`. There is no path in `ExportCell` that carries a display string
 * for a number, so "full precision, unrounded, never a display string" is a type property
 * rather than a discipline. Decimal strings are parsed once, by `parseDecimal`, which
 * returns `null` and never `0` on anything it cannot read.
 *
 * **The undefined values leave as `{ kind: 'missing' }` and never as zero.** That is the
 * half of ADR 0061 and ADR 0060 that survives the crossing: a CSV in which an unmeasured
 * jobber realisation and a route that recovered nothing both read `0` has destroyed the
 * distinction the routing decision rests on, and the recipient of that CSV is frequently a
 * jobber negotiation. `not-reported` is the state used throughout, because these are
 * measures nobody has evidence for rather than positions nobody ranged.
 *
 * **A perimeter this read did not ask for is `observed-in` and never `equals`.** None of
 * these operations takes a country, a season or an as-of parameter, so a sheet saying
 * `country_key equals CO` would tell its reader that Colombia was *chosen* — which reads
 * as "the other markets were excluded" when the truth is "the other markets returned
 * nothing". A file outlives the room it was downloaded in (`docs/adr/0083`).
 */

import { callOperation, callOperationCsv, collectPages } from '@/api';
import {
  consolidationLinesReport, exportConsolidationDoors, exportConsolidationLines, listCadenceProposals, listConsolidationDoors, listConsolidationLines, listConsolidationOptions,
  listConsolidationRuns, listD4Evidence, listHandoverReadiness, listOverrideRate,
  exitListPlans, listReturns, listRouting, listTerminalStock, listUnsignedPlans, exitReadPlan,
  type CadenceProposalSummary, type ConsolidationDoorSummary, type ConsolidationLineSummary, type ConsolidationReportRow,
  type ConsolidationOptionSummary, type D4EvidenceRow,
  type ExitPlanSummary, type HandoverReadinessSummary, type OverrideRate, type ReturnDispositionSummary,
  type RoutingSummary, type TerminalPositionSummary, type UnsignedPlanSummary,
} from '@/api/generated';
import type { ChartExportMeta } from '@/charts';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion, isAbsent } from '@/export';
import { SIZE_EXPORT_COLUMNS, SIZE_EXPORT_KEYS, sizeExportCells, sizeName } from '@/planning';
import { NO_RUN, oneRun, readConsolidationRunParameters, withParametersInForce } from '@/planning';
import type {
  ExportCell, ExportRequest, Provenance, ProvenanceFilter,
} from '@/export';

import {
  d4Delta, d4ReasonCodes, d4Verdict, lifecycleStandings, OPTION_PAGE_ROWS, optionDirectory, orderStranded,
  parseDecimal, RETURNS_SEGREGATION, strandedLots, type LifecycleStanding, type StrandedLot,
  cadenceStateLabelKey, counterfactualLabelKey, gradeLabelKey, handoverStateLabelKey, lifecycleLabelKey, nodeRoleLabelKey,
  realisationBasisLabelKey, reasonLabelKey, returnDispositionLabelKey, routeLabelKey, routeUnavailableLabelKey,
} from './api';
import { exportDisplay, exportDisplayOf } from '@/planning/words';
import { CONSOLIDATION_JOB } from './ConsolidationRunPicker';
import type { ExportScope } from '../manifest';
import { readExitScope } from './scope';
import { exportMarketSeason, marketQuery, marketSeasonQuery, withMarketSeason, type MarketSeason } from './market-season';
import { readDoorDrill } from './door-drill';
import { readRunDoorsNarrowing, readRunLanesNarrowing, runDoorsNarrowingQuery, runLanesNarrowingQuery } from './kpi-links';
import { readPlanDrill, readProposalDrill } from './drill-paths';
import { CONSOLIDATION_ATTRIBUTE_COLUMNS, consolidationAttributeCells, indexConsolidationReport } from './report-attributes';
import { doorsFromCsv, linesFromCsv } from './run-list-csv';
import { TRANCHE_WEEK_EXPORT_COLUMN, trancheWeekExportCell, trancheWeekOf } from './tranche-week';

/** The build that produced the file — `buildVersion` in `@/export`, `docs/adr/9007`. */
const PLATFORM_VERSION = buildVersion();

/** A number that may be undefined. Never `?? 0` — see ADR 0061. */
const numberCell = (value: number | null): ExportCell =>
  value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value };

const integerCell = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'integer', raw: value };

const moneyCell = (value: number | null, currency: string): ExportCell =>
  value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'money', raw: value, currency };

/** A code is a label cell carrying the code beside it, so a filter can be rebuilt. */
const codeCell = (code: string | null | undefined): ExportCell =>
  code === null || code === undefined || code.trim() === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'label', value: code, code };

/**
 * The lane book's and the door table's codes in the words their screens draw them in
 * (`./consolidation-panels.tsx`), for the Excel only: the CSV keeps the code (`docs/adr/9041`).
 * A code this build does not know stays the code.
 */
const REVENUE_BASIS_WORDS: Readonly<Record<string, string>> = {
  size_curve_split: 'exit.revenueBasis.sizeCurveSplit',
  lost_sales_corrected: 'exit.revenueBasis.lostSalesCorrected',
};
const ELASTICITY_BASIS_WORDS: Readonly<Record<string, string>> = {
  fallback: 'exit.elasticityBasis.fallback',
  fitted: 'exit.elasticityBasis.fitted',
  not_needed: 'exit.elasticityBasis.notNeeded',
};
const LANE_STATE_WORDS: Readonly<Record<string, string>> = {
  proposed: 'exit.state.laneProposed',
  withheld: 'exit.state.laneWithheld',
};
const DOOR_STATE_WORDS: Readonly<Record<string, string>> = {
  run_broken: 'exit.state.runBroken',
  run_complete: 'exit.state.runComplete',
};

/** A boolean the sheet must be able to filter on, as the two words it is. */
const flagCell = (value: boolean, whenTrue: string, whenFalse: string): ExportCell =>
  ({ kind: 'label', value: value ? whenTrue : whenFalse, code: value ? whenTrue : whenFalse });

/**
 * A three-state fact. `null` leaves as `missing`, never as `false`.
 *
 * The whole argument of the stranded-lot join, at the boundary: an option nobody could
 * judge is not an option that came out safe, and a spreadsheet column of `TRUE`/`FALSE`
 * would make the third state invisible to every reader downstream of it.
 */
const factCell = (value: boolean | null, whenTrue: string, whenFalse: string): ExportCell =>
  value === null ? { kind: 'missing', state: 'not-reported' } : flagCell(value, whenTrue, whenFalse);

/**
 * A filter describing what a LIVE read's rows turned out to span, not what it asked for.
 *
 * Empty in, nothing out: an `observed-in` row with no values would assert a span nobody
 * measured.
 */
function observedPerimeter(
  field: string, values: readonly string[],
): readonly ProvenanceFilter[] {
  return values.length === 0 ? [] : [{ field, operator: 'observed-in', value: values.join(', ') }];
}

/** Distinct, sorted, blanks dropped. The order is the data's, not the page's. */
function distinct(values: readonly (string | null | undefined)[]): readonly string[] {
  return [...new Set(
    values.filter((value): value is string => typeof value === 'string' && value.trim() !== ''),
  )].sort();
}

/**
 * The window a set of dated rows actually occupies.
 *
 * An empty set gets an empty window rather than a plausible fortnight: a date range on a
 * file with no rows is a claim about a measurement nobody took.
 */
function observedWindow(dates: readonly string[]): { readonly from: string; readonly to: string } {
  const sorted = distinct(dates);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return first === undefined || last === undefined ? { from: '', to: '' } : { from: first, to: last };
}

const provenance = (
  measures: readonly { measure: string; definition: string }[],
  perimeter: {
    readonly filters: readonly ProvenanceFilter[];
    readonly timeWindow: { readonly from: string; readonly to: string };
    /** The markets the rows come from: exactly one names the file's market (`docs/adr/9048`). */
    readonly markets?: readonly string[];
  },
): Provenance => ({
  filters: perimeter.filters,
  hierarchyNode: perimeter.markets?.length === 1 ? perimeter.markets[0] ?? NOT_PUBLISHED : NOT_PUBLISHED,
  timeWindow: perimeter.timeWindow.from === '' ? NOT_PUBLISHED : perimeter.timeWindow,
  scenario: NOT_PUBLISHED,
  planVersionId: NOT_PUBLISHED,
  measureDefinitions: measures.map((entry) => ({
    measure: entry.measure,
    definition: entry.definition,
    registerVersion: '',
  })),
  generatedAt: new Date().toISOString(),
  generatedBy: NO_PRINCIPAL,
  platformVersion: PLATFORM_VERSION,
  // No `illustrative` entry on any sheet in this file: every row came from the pinned
  // client. The tenth provenance field is absent rather than empty.
});

/**
 * The terminal-stock definition, cited verbatim on every export that carries TSR.
 *
 * ADR 0060: this chapter reads the registered measure and does not restate it, so the
 * string below is the register's and appears in exactly one place in this feature. The
 * store-side clause is not a caveat added here — it is part of the registered definition,
 * because reqs/05 §3.1 counts only doors with on-hand above zero.
 */
const TSR_DEFINITION = {
  measure: 'exit.terminal_stock_ratio',
  definition:
    'Residual saleable units at the handover date over total season intake units, counting only saleable condition grades and only doors with on-hand above zero. Distribution-centre and in-transit stock are outside the measure. Undefined on zero intake.',
} as const;

/**
 * The depletion rate every surface in this chapter measures a position against.
 *
 * Hoisted to a constant because it is now cited by two artefacts rather than one — the
 * plan book's sheet and the cadence chart's tile export — and a definition restated is a
 * definition that can drift. `pathChartExportMeta` in chapter 04 took the same road for
 * the same reason.
 */
const REQUIRED_RATE_DEFINITION = {
  measure: 'exit.required_depletion_rate',
  definition:
    'Remaining units less target residual, over remaining weeks to the handover date. Undefined at the handover week.',
} as const;

/** Every read in this feature is unfiltered, and the sheets say so in one voice. */
const NOT_APPLIED: readonly ProvenanceFilter[] = [
  { field: 'country_key', operator: 'not-applied', value: '' },
  { field: 'option_id', operator: 'not-applied', value: '' },
];

/** The door ranking measure, cited verbatim by the doors sheet and by the platform's own file of it. */
const SELL_THROUGH_DEFINITION = {
  measure: 'exit.sell_through_per_capacity',
  definition: 'reqs/05 §3.4\'s door ranking measure for consolidation. Undefined — never zero — on a door with no capacity on record, which has no ranking.',
} as const;

/** The lane's holding cost, cited verbatim by the lane book and by the platform's own file of it. */
const LANE_COST_DEFINITION = {
  measure: 'exit.holding_cost_per_unit_week',
  definition: 'Holding, handling and freight cost per unit per week for the lane, from the D8 cost feed. Undefined — never zero — on a lane the feed has not costed; the row carries the reason code that makes it so.',
} as const;

// ── X1 — exit plans against terminal-stock targets ───────────────────────────

/** X1 — the plan book, read from `GET /exit/plans`. */
export async function exitPlanExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return exitPlanRequest(await collectPages(exitListPlans, { query: marketSeasonQuery(scope) }), scope);
}

/**
 * One plan's page, `/exit/plans/:exitPlanId` — `GET /exit/plans/{exit_plan_id}`, the read the
 * page makes, in the plan book's own sheet. No plan on the path, no request.
 */
export async function exitPlanDetailExport(): Promise<ExportRequest> {
  const exitPlanId = readPlanDrill(typeof window === 'undefined' ? '' : window.location.pathname);
  const plans = exitPlanId === null
    ? []
    : [await callOperation(exitReadPlan, { path: { exit_plan_id: exitPlanId } })];
  return { ...exitPlanRequest(plans), id: 'exit.plans.detail' };
}

/**
 * The plans awaiting a signature — the Unsigned view's export (client rule 3, 2026-09-30: the gap
 * list left the plan book for a page of its own). The same read and sheet as the panel's table.
 */
export async function unsignedPlansExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return unsignedPlansRequest(await collectPages(listUnsignedPlans, { query: marketSeasonQuery(scope) }), scope);
}

/** The measured terminal position — the Terminal position view's export, the panel's own sheet. */
export async function terminalPositionExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return terminalPositionsRequest(await collectPages(listTerminalStock, { query: marketSeasonQuery(scope) }), scope);
}

/**
 * The as-of date the override-rate read is asked at. The endpoint requires one and the queue has
 * no single week to take it from, so wall-clock today is sent — the known limitation `docs/adr/7000`
 * records for the endpoint owner, not a considered answer. One spelling, shared by the panel and
 * this export so the two ask the same question.
 */
export function overrideRateAsOfDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The Override history view's export: the published counts, one row per reason code × disposition. */
export async function overrideRateExport(given?: ExportScope): Promise<ExportRequest> {
  const asOfDate = overrideRateAsOfDate();
  const scope = exportMarketSeason(given);
  return overrideRateRequest(
    await collectPages(listOverrideRate, { query: { as_of_date: asOfDate, ...marketQuery(scope) } }), asOfDate, scope,
  );
}

/**
 * The override counts as published — never divided into a rate here (reqs/07 §3.5; the rate is
 * `planning.programme.override_rate`'s to compute).
 */
/**
 * The sheet with the market it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function overrideRateRequest(rows: readonly OverrideRate[], asOfDate: string, scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(overrideRateSheet(rows, asOfDate), scope, false);
}

function overrideRateSheet(rows: readonly OverrideRate[], asOfDate: string): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    { kind: 'label', value: row.reason_code, code: row.reason_code },
    { kind: 'label', value: row.disposition, code: row.disposition },
    { kind: 'number', raw: row.disposition_count },
  ]);
  const filters: readonly ProvenanceFilter[] = [{ field: 'as_of_date', operator: 'equals', value: asOfDate }];
  return {
    id: 'exit.cadence.overrides',
    titleKey: 'exit.overrideRate.title',
    columns: [
      { key: 'reason_code', headerKey: 'exit.column.reasonCode', descriptionKey: 'exit.columnGuide.overrides.reasonCode', kind: 'label', display: exportDisplay(reasonLabelKey) },
      { key: 'disposition', headerKey: 'exit.column.disposition', descriptionKey: 'exit.columnGuide.overrides.disposition', kind: 'label' },
      { key: 'disposition_count', headerKey: 'exit.column.dispositionCount', descriptionKey: 'exit.columnGuide.overrides.count', kind: 'number' },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'override_count',
        definition: 'How many cadence proposals of a reason code received each disposition, as the platform stores the numerator and denominator pairs. No rate is computed here: the ratio a reader wants depends on the denominator they choose.',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [{ key: 'reason_code', direction: 'asc' }],
      visibleColumns: ['reason_code', 'disposition', 'disposition_count'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The plan book's sheet over rows already read — the route's export above, and the CSV under
 * the capped table (`docs/adr/9008`), which hands in the rows it draws so the file and the
 * table cannot disagree.
 */
/**
 * The sheet with the market and season it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function exitPlanRequest(plans: readonly ExitPlanSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(exitPlanSheet(plans), scope, true);
}

function exitPlanSheet(plans: readonly ExitPlanSummary[]): ExportRequest {
  const rows: ExportCell[][] = plans.map((row) => [
    { kind: 'text', value: row.exit_plan_id },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.country_key },
    { kind: 'text', value: row.season_code },
    codeCell(row.lifecycle_class),
    { kind: 'date', iso: row.handover_date },
    integerCell(row.weeks_to_handover),
    integerCell(row.residual_units),
    // The one that matters. An option with no intake exports as missing, never as 0.
    numberCell(parseDecimal(row.terminal_stock_ratio)),
    numberCell(parseDecimal(row.target_ratio)),
    numberCell(parseDecimal(row.terminal_sell_through)),
    numberCell(parseDecimal(row.required_depletion_rate)),
    integerCell(row.projected_residual_units),
    { kind: 'integer', raw: row.target_residual_units },
    moneyCell(parseDecimal(row.price_floor_amount), row.currency_code),
    codeCell(row.exit_route_code),
    flagCell(row.protected_from_markdown, 'protected', 'reprisable'),
    flagCell(row.merchant_signed_off, 'signed', 'unsigned'),
    flagCell(row.finance_signed_off, 'signed', 'unsigned'),
  ]);
  return {
    id: 'exit.plans.table',
    titleKey: 'exit.plans.title',
    columns: [
      { key: 'exit_plan_id', headerKey: 'exit.column.exitPlanId', descriptionKey: 'exit.columnGuide.common.exitPlanId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'season_code', headerKey: 'exit.column.seasonCode', descriptionKey: 'exit.columnGuide.plan.seasonCode', kind: 'text' },
      { key: 'lifecycle_class', headerKey: 'exit.column.lifecycleClass', descriptionKey: 'exit.columnGuide.plan.lifecycleClass', kind: 'label', display: exportDisplay(lifecycleLabelKey) },
      { key: 'handover_date', headerKey: 'exit.column.handoverDate', descriptionKey: 'exit.columnGuide.plan.handoverDate', kind: 'date' },
      { key: 'weeks_to_handover', headerKey: 'exit.column.weeksToHandover', descriptionKey: 'exit.columnGuide.plan.weeksToHandover', kind: 'integer', polarity: 'higher-is-better' },
      { key: 'residual_units', headerKey: 'exit.column.residualUnits', descriptionKey: 'exit.columnGuide.plan.residualUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'terminal_stock_ratio', headerKey: 'exit.column.terminalStockRatio', descriptionKey: 'exit.columnGuide.plan.terminalStockRatio', kind: 'number', polarity: 'lower-is-better' },
      { key: 'target_ratio', headerKey: 'exit.column.targetRatio', descriptionKey: 'exit.columnGuide.plan.targetRatio', kind: 'number', polarity: 'lower-is-better' },
      { key: 'terminal_sell_through', headerKey: 'exit.column.terminalSellThrough', descriptionKey: 'exit.columnGuide.plan.terminalSellThrough', kind: 'number', polarity: 'higher-is-better' },
      { key: 'required_depletion_rate', headerKey: 'exit.column.requiredRate', descriptionKey: 'exit.columnGuide.plan.requiredDepletionRate', kind: 'number', polarity: 'none' },
      { key: 'projected_residual_units', headerKey: 'exit.column.projectedResidual', descriptionKey: 'exit.columnGuide.plan.projectedResidualUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'target_residual_units', headerKey: 'exit.column.targetResidualUnits', descriptionKey: 'exit.columnGuide.plan.targetResidualUnits', kind: 'integer' },
      { key: 'price_floor_amount', headerKey: 'exit.column.priceFloor', descriptionKey: 'exit.columnGuide.plan.priceFloor', kind: 'money', currency: 'COP' },
      { key: 'exit_route_code', headerKey: 'exit.column.exitRoute', descriptionKey: 'exit.columnGuide.plan.exitRoute', kind: 'label', display: exportDisplay(routeLabelKey) },
      { key: 'protected_from_markdown', headerKey: 'exit.column.protectedFlag', descriptionKey: 'exit.columnGuide.plan.protected', kind: 'label' },
      { key: 'merchant_signed_off', headerKey: 'exit.column.merchantSignOff', descriptionKey: 'exit.columnGuide.plan.merchantSignedOff', kind: 'label' },
      { key: 'finance_signed_off', headerKey: 'exit.column.financeSignOff', descriptionKey: 'exit.columnGuide.plan.financeSignedOff', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([TSR_DEFINITION, REQUIRED_RATE_DEFINITION], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(plans.map((row) => row.country_key))),
        ...observedPerimeter('season_code', distinct(plans.map((row) => row.season_code))),
      ],
      timeWindow: observedWindow(plans.map((row) => row.handover_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'terminal_stock_ratio', direction: 'desc' }],
      visibleColumns: ['exit_plan_id', 'option_id', 'country_key', 'season_code', 'lifecycle_class', 'handover_date', 'weeks_to_handover', 'residual_units', 'terminal_stock_ratio', 'target_ratio', 'terminal_sell_through', 'required_depletion_rate', 'projected_residual_units', 'target_residual_units', 'price_floor_amount', 'exit_route_code', 'protected_from_markdown', 'merchant_signed_off', 'finance_signed_off'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The measured terminal position, as the plan screen's terminal table draws it — the CSV
 * under that table (`docs/adr/9008`). The route's own export is the plan book; this is the
 * second long table on the screen.
 *
 * The all-clusters row leaves as `''` in `cluster_id`, as the platform publishes it — it is
 * a real value, not a missing one, which is why it is text and not `codeCell`. The verdict is
 * the STORED one (ADR 0060); nothing is compared here.
 */
/**
 * The sheet with the market and season it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function terminalPositionsRequest(positions: readonly TerminalPositionSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(terminalPositionsSheet(positions), scope, true);
}

function terminalPositionsSheet(positions: readonly TerminalPositionSummary[]): ExportRequest {
  const rows: ExportCell[][] = positions.map((row) => [
    { kind: 'text', value: row.exit_plan_id },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.cluster_id },
    { kind: 'text', value: row.country_key },
    { kind: 'text', value: row.season_code },
    { kind: 'text', value: row.as_of_week },
    codeCell(row.lifecycle_class),
    { kind: 'integer', raw: row.saleable_units },
    { kind: 'integer', raw: row.stocking_doors },
    { kind: 'integer', raw: row.intake_units },
    numberCell(parseDecimal(row.terminal_stock_ratio)),
    numberCell(parseDecimal(row.target_ratio)),
    numberCell(parseDecimal(row.terminal_sell_through ?? null)),
    { kind: 'integer', raw: row.projected_residual_units },
    { kind: 'integer', raw: row.target_residual_units },
    { kind: 'text', value: row.intake_definition_id },
    flagCell(row.breaches_target, 'breaches_target', 'within_target'),
  ]);
  return {
    id: 'exit.plans.terminal',
    titleKey: 'exit.terminal.title',
    columns: [
      { key: 'exit_plan_id', headerKey: 'exit.column.exitPlanId', descriptionKey: 'exit.columnGuide.common.exitPlanId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'cluster_id', headerKey: 'exit.column.cluster', descriptionKey: 'exit.columnGuide.terminal.clusterId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'season_code', headerKey: 'exit.column.seasonCode', descriptionKey: 'exit.columnGuide.plan.seasonCode', kind: 'text' },
      { key: 'as_of_week', headerKey: 'exit.column.asOfWeek', descriptionKey: 'exit.columnGuide.terminal.asOfWeek', kind: 'text' },
      { key: 'lifecycle_class', headerKey: 'exit.column.lifecycleClass', descriptionKey: 'exit.columnGuide.plan.lifecycleClass', kind: 'label', display: exportDisplay(lifecycleLabelKey) },
      { key: 'saleable_units', headerKey: 'exit.column.saleableUnits', descriptionKey: 'exit.columnGuide.terminal.saleableUnits', kind: 'integer' },
      { key: 'stocking_doors', headerKey: 'exit.column.stockingDoors', descriptionKey: 'exit.columnGuide.terminal.stockingDoors', kind: 'integer' },
      { key: 'intake_units', headerKey: 'exit.column.intakeUnits', descriptionKey: 'exit.columnGuide.terminal.intakeUnits', kind: 'integer' },
      { key: 'terminal_stock_ratio', headerKey: 'exit.column.terminalStockRatio', descriptionKey: 'exit.columnGuide.terminal.terminalStockRatio', kind: 'number', polarity: 'lower-is-better' },
      { key: 'target_ratio', headerKey: 'exit.column.targetRatio', descriptionKey: 'exit.columnGuide.plan.targetRatio', kind: 'number', polarity: 'lower-is-better' },
      { key: 'terminal_sell_through', headerKey: 'exit.column.terminalSellThrough', descriptionKey: 'exit.columnGuide.plan.terminalSellThrough', kind: 'number', polarity: 'higher-is-better' },
      { key: 'projected_residual_units', headerKey: 'exit.column.projectedResidual', descriptionKey: 'exit.columnGuide.terminal.projectedResidualUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'target_residual_units', headerKey: 'exit.column.targetResidualUnits', descriptionKey: 'exit.columnGuide.plan.targetResidualUnits', kind: 'integer' },
      { key: 'intake_definition_id', headerKey: 'exit.column.intakeDefinition', descriptionKey: 'exit.columnGuide.terminal.intakeDefinition', kind: 'text' },
      { key: 'standing', headerKey: 'exit.column.standing', descriptionKey: 'exit.columnGuide.terminal.standing', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([TSR_DEFINITION], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(positions.map((row) => row.country_key))),
        ...observedPerimeter('season_code', distinct(positions.map((row) => row.season_code))),
        ...observedPerimeter('intake_definition_id', distinct(positions.map((row) => row.intake_definition_id))),
      ],
      timeWindow: observedWindow(positions.map((row) => row.handover_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      // The order the read delivered and the table draws; the table applies no sort.
      sort: [],
      visibleColumns: ['exit_plan_id', 'option_id', 'cluster_id', 'country_key', 'season_code', 'as_of_week', 'lifecycle_class', 'saleable_units', 'stocking_doors', 'intake_units', 'terminal_stock_ratio', 'target_ratio', 'terminal_sell_through', 'projected_residual_units', 'target_residual_units', 'intake_definition_id', 'standing'],
      drillPath: ['option', 'cluster'],
      densityMode: 'compact',
    },
  };
}

/** The plans still awaiting a signature — the CSV under that table (`docs/adr/9008`). */
/**
 * The sheet with the market and season it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function unsignedPlansRequest(plans: readonly UnsignedPlanSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(unsignedPlansSheet(plans), scope, true);
}

function unsignedPlansSheet(plans: readonly UnsignedPlanSummary[]): ExportRequest {
  const rows: ExportCell[][] = plans.map((row) => [
    { kind: 'text', value: row.exit_plan_id },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.country_key },
    { kind: 'text', value: row.season_code },
    { kind: 'date', iso: row.handover_date },
    flagCell(row.merchant_signed_off, 'signed', 'unsigned'),
    flagCell(row.finance_signed_off, 'signed', 'unsigned'),
  ]);
  return {
    id: 'exit.plans.unsigned',
    titleKey: 'exit.unsigned.title',
    columns: [
      { key: 'exit_plan_id', headerKey: 'exit.column.exitPlanId', descriptionKey: 'exit.columnGuide.common.exitPlanId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.country', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'season_code', headerKey: 'exit.column.season', descriptionKey: 'exit.columnGuide.plan.seasonCode', kind: 'text' },
      { key: 'handover_date', headerKey: 'exit.column.handoverDate', descriptionKey: 'exit.columnGuide.plan.handoverDate', kind: 'date' },
      { key: 'merchant_signed_off', headerKey: 'exit.column.merchantSignOff', descriptionKey: 'exit.columnGuide.unsigned.merchant', kind: 'label' },
      { key: 'finance_signed_off', headerKey: 'exit.column.financeSignOff', descriptionKey: 'exit.columnGuide.unsigned.finance', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.plan_sign_off',
      definition: 'An exit plan is signed when both the merchant and finance have signed it off. Each signature is published separately; a plan awaiting both appears once, with both columns unsigned.',
    }], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(plans.map((row) => row.country_key))),
        ...observedPerimeter('season_code', distinct(plans.map((row) => row.season_code))),
      ],
      timeWindow: observedWindow(plans.map((row) => row.handover_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [],
      visibleColumns: ['exit_plan_id', 'option_id', 'country_key', 'season_code', 'handover_date', 'merchant_signed_off', 'finance_signed_off'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

// ── X2 — the cadence queue ───────────────────────────────────────────────────

/**
 * X2 — the cadence queue, with the counterfactual on the row.
 *
 * Both branches of reqs/05 §3.2 step 5 are columns and the winner is a third, because a
 * spreadsheet carrying only the proposed rung is a spreadsheet a country manager cannot
 * argue with — and reqs/05 §7 puts markdown authority with country management.
 */
export async function cadenceExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return cadenceRequest(await collectPages(listCadenceProposals, { query: marketQuery(scope) }), scope);
}

/**
 * One cadence proposal's page, `/exit/cadence/:proposalId` — the same queue read the page makes,
 * narrowed by the platform to that proposal, in the queue's own sheet. No proposal on the path,
 * no request: an empty sheet rather than the whole queue under a single proposal's name.
 */
export async function cadenceProposalExport(): Promise<ExportRequest> {
  const proposalId = readProposalDrill(typeof window === 'undefined' ? '' : window.location.pathname);
  const rows = proposalId === null
    ? []
    : await collectPages(listCadenceProposals, { query: { proposal_id: proposalId } });
  return { ...cadenceRequest(rows), id: 'exit.cadence.proposal' };
}

/** The cadence queue's sheet over rows already read (`docs/adr/9008`). */
/**
 * The sheet with the market it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function cadenceRequest(proposals: readonly CadenceProposalSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(cadenceSheet(proposals), scope, false);
}

function cadenceSheet(proposals: readonly CadenceProposalSummary[]): ExportRequest {
  const rows: ExportCell[][] = proposals.map((row) => [
    { kind: 'text', value: row.proposal_id },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.cluster_id },
    { kind: 'text', value: row.country_key },
    { kind: 'date', iso: row.as_of_date },
    moneyCell(parseDecimal(row.from_price_amount), row.currency_code),
    moneyCell(parseDecimal(row.to_price_amount), row.currency_code),
    numberCell(parseDecimal(row.required_rate)),
    numberCell(parseDecimal(row.forecast_rate)),
    numberCell(parseDecimal(row.implied_rate)),
    numberCell(parseDecimal(row.elasticity_coefficient)),
    { kind: 'text', value: row.elasticity_model_version_id },
    moneyCell(parseDecimal(row.step_value_amount), row.currency_code),
    moneyCell(parseDecimal(row.hold_value_amount), row.currency_code),
    codeCell(row.counterfactual_winner),
    moneyCell(parseDecimal(row.carryover_npv_amount), row.currency_code),
    codeCell(row.reason_code),
    codeCell(row.state),
  ]);
  return {
    id: 'exit.cadence.table',
    titleKey: 'exit.cadence.title',
    columns: [
      { key: 'proposal_id', headerKey: 'exit.column.proposalId', descriptionKey: 'exit.columnGuide.cadence.proposalId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'cluster_id', headerKey: 'exit.column.cluster', descriptionKey: 'exit.columnGuide.cadence.clusterId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'as_of_date', headerKey: 'exit.column.asOfDate', descriptionKey: 'exit.columnGuide.cadence.asOfDate', kind: 'date' },
      { key: 'from_price_amount', headerKey: 'exit.column.fromRung', descriptionKey: 'exit.columnGuide.cadence.fromPrice', kind: 'money', currency: 'COP' },
      { key: 'to_price_amount', headerKey: 'exit.column.toRung', descriptionKey: 'exit.columnGuide.cadence.toPrice', kind: 'money', currency: 'COP' },
      { key: 'required_rate', headerKey: 'exit.column.requiredRate', descriptionKey: 'exit.columnGuide.cadence.requiredRate', kind: 'number', polarity: 'none' },
      { key: 'forecast_rate', headerKey: 'exit.column.forecastRate', descriptionKey: 'exit.columnGuide.cadence.forecastRate', kind: 'number', polarity: 'higher-is-better' },
      { key: 'implied_rate', headerKey: 'exit.column.impliedRate', descriptionKey: 'exit.columnGuide.cadence.impliedRate', kind: 'number', polarity: 'higher-is-better' },
      { key: 'elasticity_coefficient', headerKey: 'exit.column.elasticity', descriptionKey: 'exit.columnGuide.cadence.elasticity', kind: 'number', polarity: 'none' },
      { key: 'elasticity_model_version_id', headerKey: 'exit.column.elasticityModel', descriptionKey: 'exit.columnGuide.cadence.elasticityModel', kind: 'text' },
      { key: 'step_value_amount', headerKey: 'exit.column.stepValue', descriptionKey: 'exit.columnGuide.cadence.stepValue', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'hold_value_amount', headerKey: 'exit.column.holdValue', descriptionKey: 'exit.columnGuide.cadence.holdValue', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'counterfactual_winner', headerKey: 'exit.column.cheaperPath', descriptionKey: 'exit.columnGuide.cadence.cheaperPath', kind: 'label', display: exportDisplay(counterfactualLabelKey) },
      { key: 'carryover_npv_amount', headerKey: 'exit.column.carryoverNpv', descriptionKey: 'exit.columnGuide.cadence.carryoverNpv', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'reason_code', headerKey: 'exit.column.reasonCode', descriptionKey: 'exit.columnGuide.cadence.reasonCode', kind: 'label', display: exportDisplay(reasonLabelKey) },
      { key: 'state', headerKey: 'exit.column.state', descriptionKey: 'exit.columnGuide.cadence.state', kind: 'label', display: exportDisplay(cadenceStateLabelKey) },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.clearance_depth',
      definition: 'Current rung against the exit-route indifference price for the lot, at the declared markdown variant.',
    }, {
      measure: 'exit.hold_versus_step',
      definition: 'Value of holding one more week at the current rung, priced with weekly holding cost and the deeper rung then required, against stepping now. Undefined — never zero — on a proposal at its floor or awaiting recovery, where no comparison was run.',
    }], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(proposals.map((row) => row.country_key))),
        ...observedPerimeter('as_of_date', distinct(proposals.map((row) => row.as_of_date))),
      ],
      timeWindow: observedWindow(proposals.map((row) => row.as_of_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'proposal_id', direction: 'asc' }],
      visibleColumns: ['proposal_id', 'option_id', 'cluster_id', 'country_key', 'as_of_date', 'from_price_amount', 'to_price_amount', 'required_rate', 'forecast_rate', 'implied_rate', 'elasticity_coefficient', 'elasticity_model_version_id', 'step_value_amount', 'hold_value_amount', 'counterfactual_winner', 'carryover_npv_amount', 'reason_code', 'state'],
      drillPath: ['option'],
      densityMode: 'compact',
    },
  };
}

/**
 * The rate-gap panel's own export, for the tile that renders it — `docs/adr/9000`.
 *
 * Built from the same `listCadenceProposals` rows the panel is already showing and through
 * the same perimeter helpers `cadenceExport` uses. The measure cited is the one the CHART draws rather than the two
 * the sheet carries: a provenance page naming `hold_versus_step` beside a file of rate
 * gaps would describe a column that is not in it.
 */
export function cadenceGapChartExportMeta(
  proposals: readonly CadenceProposalSummary[],
): ChartExportMeta {
  const filters: readonly ProvenanceFilter[] = [
    ...NOT_APPLIED,
    ...observedPerimeter('country_key', distinct(proposals.map((row) => row.country_key))),
    ...observedPerimeter('as_of_date', distinct(proposals.map((row) => row.as_of_date))),
  ];
  return {
    id: 'exit.cadence.table',
    titleKey: 'exit.cadence.chartTitle',
    provenance: provenance([REQUIRED_RATE_DEFINITION, {
      measure: 'exit.rate_gap',
      definition:
        'The proposal\'s forecast depletion rate less the rate its plan requires, in pairs a week, so a position behind plan is negative. Both rates are published on the proposal and neither is derived here; a proposal publishing either without the other is undefined rather than nought, because nought gap is a position running exactly to plan.',
    }], {
      filters,
      timeWindow: observedWindow(proposals.map((row) => row.as_of_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'proposal_id', direction: 'asc' }],
      // The queue's drill grain, as the route declares it. A file exported from the chart
      // is at the same level the panel is read at.
      drillPath: ['option'],
      densityMode: 'compact',
    },
  };
}

// ── X3 — the consolidation lane book ─────────────────────────────────────────

/**
 * X3 — the candidate lane list, at the grain a WMS integrator needs it.
 *
 * It carries the WITHHELD lanes as well as the proposed ones — the API returns them
 * deliberately and this handler does not filter them out. A CSV of proposals alone cannot
 * answer "why did a door full of broken runs produce no recommendation", which is the
 * question reqs/05 §5 D8 is the feed for.
 *
 * **An unpriced lane leaves as `missing`, never as `0`.** `lane_cost_amount` is null on
 * every lane the D8 feed has not costed, and a spreadsheet in which an unpriced lane and a
 * free one both read `0` makes every transfer look profitable — reqs/05 §7's
 * over-consolidation failure, now portable. `reason_code` rides along as the platform's
 * own datum so the recipient can tell `lane_not_costed` from `transfer_uneconomic`.
 */
/**
 * `request` with the «Parametri in vigore» sheet of the one consolidation run its rows come from
 * (bata-reqs ADR 9001 decision 5): that run's own record, or a sentence saying why there is none — no
 * run, several, not recorded, not published yet. Never today's registry. Applied last, after any
 * re-titling, so the file's own sheet keeps its title. Never rejects.
 */
export async function withRunParameters(request: ExportRequest, runIds: Iterable<string | null>): Promise<ExportRequest> {
  const run = oneRun([...runIds].filter((runId): runId is string => runId !== null && runId !== ''));
  return withParametersInForce(request, typeof run === 'string' ? await readConsolidationRunParameters(run) : run);
}

export async function consolidationExport(): Promise<ExportRequest> {
  // The perimeter the screen is showing: the same run, and the same option once one is
  // chosen. With no option chosen the export is the run's whole lane book, which is what a
  // WMS integrator asks for. With no run chosen the screen draws no lane at all, so neither
  // does the export: the read is never issued unbounded (CLAUDE.md §3.4, rule 2).
  const scope = readExitScope(typeof window === 'undefined' ? '' : window.location.search);
  const lines = scope.consolidationRunId === null ? [] : await collectPages(listConsolidationLines, {
    query: {
      run_id: scope.consolidationRunId,
      ...(scope.consolidationOptionId === null ? {} : { option_id: scope.consolidationOptionId }),
    },
  });
  const request = await consolidationReportRequest(lines);
  // The read was filtered by run and, once chosen, by option: the file says so, and is named for its run.
  return withRunParameters(scope.consolidationRunId === null ? request : statingFilters(request, [
    { field: 'run_id', operator: 'equals', value: scope.consolidationRunId },
    ...(scope.consolidationOptionId === null ? [] : [{ field: 'option_id', operator: 'equals', value: scope.consolidationOptionId }]),
  ]), [scope.consolidationRunId]);
}

/**
 * The lane book's file with the run's lines report joined in (`./report-attributes.ts`, issue 470):
 * the report of every run the lanes come from, read in full. Never rejects: an unread report — a
 * platform before it, or `planning.inseason.report_unavailable` — leaves every lane's report columns
 * absent with `not_in_report`, and the file keeps every lane.
 */
export async function consolidationReportRequest(lines: readonly ConsolidationLineSummary[]): Promise<ExportRequest> {
  const runIds = [...new Set(lines.map((line) => line.run_id))];
  const reports = await Promise.all(runIds.map(async (runId) => {
    try {
      return await collectPages(consolidationLinesReport, { path: { run_id: runId } });
    } catch {
      return [];
    }
  }));
  return consolidationRequest(lines, indexConsolidationReport(reports.flat()));
}

/**
 * The position run the address bar's consolidation run is paired with, as the run directory
 * states it — never inferred (ADR 7001). `null` with no run, an unknown run or an unpaired one:
 * the page draws nothing then, so neither does its export.
 */
async function pairedPositionRun(runId: string | null): Promise<string | null> {
  if (runId === null) return null;
  const runs = await collectPages(listConsolidationRuns, { query: { job_name: CONSOLIDATION_JOB } });
  return runs.find((run) => run.run_id === runId)?.position_run_id ?? null;
}

/**
 * The Estate page's registered export — the option directory of the run the URL names, every
 * option, largest residual first (`docs/adr/7001`, amendment of 2026-09-30). The page's table
 * CSV is the same sheet with its filter applied.
 */
export async function consolidationOptionsExport(): Promise<ExportRequest> {
  const scope = readExitScope(typeof window === 'undefined' ? '' : window.location.search);
  const positionRunId = await pairedPositionRun(scope.consolidationRunId);
  const options = positionRunId === null ? [] : await collectPages(listConsolidationOptions, {
    query: { run_id: positionRunId, limit: OPTION_PAGE_ROWS },
  });
  // The options are the position run's measurement, so the parameters are that run's.
  return withRunParameters(consolidationOptionsRequest(optionDirectory(options, '').ordered, ''), [positionRunId]);
}

/**
 * The Doors page's registered export — one option's doors in the run the URL names. With no
 * run or no option chosen the page draws no door, and the read is never issued unbounded
 * (CLAUDE.md §3.4, rule 2).
 */
export async function consolidationDoorsExport(): Promise<ExportRequest> {
  const scope = readExitScope(typeof window === 'undefined' ? '' : window.location.search);
  const positionRunId = scope.consolidationOptionId === null ? null : await pairedPositionRun(scope.consolidationRunId);
  const doors = positionRunId === null || scope.consolidationOptionId === null ? [] : await collectPages(listConsolidationDoors, {
    query: { run_id: positionRunId, option_id: scope.consolidationOptionId },
  });
  return withRunParameters(consolidationDoorsRequest(doors), [positionRunId]);
}

/**
 * The run-wide doors page's registered export — every door position of the run the URL names, narrowed by the filters
 * the address states (`?runIsBroken=`, `?sellThroughAbsent=`), the same read the page lists (ADR 9069). No run: the page
 * draws nothing, so neither does the export.
 */
export async function consolidationRunDoorsExport(): Promise<ExportRequest> {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const positionRunId = await pairedPositionRun(readExitScope(search).consolidationRunId);
  return positionRunId === null
    ? withParametersInForce(consolidationRunDoorsRequest([]), NO_RUN)
    : consolidationRunDoorsFileRequest(positionRunId, runDoorsNarrowingQuery(readRunDoorsNarrowing(search)));
}

/**
 * The run-wide doors page's table, as a file: the doors sheet under its own id, with its «Columns» sheet. `listed`, when the
 * rows are a run's list narrowed by the address, states the run and the filters in the file's perimeter and provenance.
 */
export function consolidationRunDoorsRequest(
  doors: readonly ConsolidationDoorSummary[], listed?: { readonly runId: string; readonly narrowing: ReturnType<typeof runDoorsNarrowingQuery> },
): ExportRequest {
  const request: ExportRequest = {
    ...consolidationDoorsRequest(doors), id: 'exit.consolidation.runDoors', titleKey: 'exit.consolidationRunDoors.title',
  };
  return listed === undefined ? request : statingRunList(request, listed.runId, listed.narrowing);
}

/**
 * The run-wide lanes page's registered export — every lane of the run the URL names, narrowed by the filters the address
 * states (`?isProposed=`, `?lanePriced=`, `?laneCostAbsent=`, `?repairsSizeRun=`), the same read the page lists (ADR 9069).
 */
export async function consolidationRunLanesExport(): Promise<ExportRequest> {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const runId = readExitScope(search).consolidationRunId;
  return runId === null
    ? withParametersInForce(runLanesRequest(await consolidationReportRequest([])), NO_RUN)
    : consolidationRunLanesFileRequest(runId, runLanesNarrowingQuery(readRunLanesNarrowing(search)));
}

/**
 * The perimeter a run-wide list's file states: the run, and every filter the list was asked for (`equals`, because these ARE
 * the read's own parameters). It also names the file — `exportFilename` takes the run from a `run_id` filter.
 */
function runListPerimeter(runId: string, narrowing: Readonly<Record<string, boolean | undefined>>): readonly ProvenanceFilter[] {
  return [
    { field: 'run_id', operator: 'equals', value: runId },
    ...Object.entries(narrowing).flatMap(([field, value]): readonly ProvenanceFilter[] => (
      value === undefined ? [] : [{ field, operator: 'equals', value: String(value) }]
    )),
  ];
}

/** A run-wide list's request with the run and the filters of the address stated, over what the rows observed of themselves. */
function statingRunList(request: ExportRequest, runId: string, narrowing: Readonly<Record<string, boolean | undefined>>): ExportRequest {
  return statingFilters(request, runListPerimeter(runId, narrowing));
}

/**
 * A request with the filters its read was ASKED for stated (`equals`): in the perimeter, which names
 * the file's run (`docs/adr/9048`), and in the provenance, where each replaces the `not-applied` or
 * observed entry of the same field.
 */
function statingFilters(request: ExportRequest, stated: readonly ProvenanceFilter[]): ExportRequest {
  const fields = new Set(stated.map((filter) => filter.field));
  return {
    ...request,
    provenance: {
      ...request.provenance,
      filters: [...stated, ...(isAbsent(request.provenance.filters) ? [] : request.provenance.filters.filter((filter) => !fields.has(filter.field)))],
    },
    perimeter: { ...request.perimeter, filters: stated },
  };
}

/**
 * The run-wide doors file: every door position the filters match, however many. The ROWS are the platform's CSV of the list
 * (platform ADR 6194 section 6): ONE request, no cursor, no page size, no ceiling in the browser. The FILE is the export module's,
 * through the same builder as every table — the table's headers, the «Columns» sheet, the provenance, the CSV and the Excel.
 * `language` is the reader's, for the words of a refusal.
 */
export async function consolidationRunDoorsFileRequest(
  positionRunId: string, narrowing: ReturnType<typeof runDoorsNarrowingQuery>, language?: string,
): Promise<ExportRequest> {
  const doors = doorsFromCsv(await callOperationCsv(
    exportConsolidationDoors, { query: { run_id: positionRunId, ...narrowing } }, language === undefined ? {} : { language },
  ));
  return withRunParameters(consolidationRunDoorsRequest(doors, { runId: positionRunId, narrowing }), [positionRunId]);
}

/** The run-wide lanes file: every lane the filters match, the rows read as the doors' are and written as the lane book's file is. */
export async function consolidationRunLanesFileRequest(
  runId: string, narrowing: ReturnType<typeof runLanesNarrowingQuery>, language?: string,
): Promise<ExportRequest> {
  const lines = linesFromCsv(await callOperationCsv(
    exportConsolidationLines, { query: { run_id: runId, ...narrowing } }, language === undefined ? {} : { language },
  ));
  return withRunParameters(runLanesRequest(await consolidationReportRequest(lines), { runId, narrowing }), [runId]);
}

/** The run-wide lanes page's table, as a file: the lane sheet under its own id, with its «Columns» sheet. */
export function runLanesRequest(
  request: ExportRequest, listed?: { readonly runId: string; readonly narrowing: ReturnType<typeof runLanesNarrowingQuery> },
): ExportRequest {
  const lanes: ExportRequest = { ...request, id: 'exit.consolidation.runLanes', titleKey: 'exit.consolidationRunLanes.title' };
  return listed === undefined ? lanes : statingRunList(lanes, listed.runId, listed.narrowing);
}

/** The lane book's sheet over rows already read (`docs/adr/9008`), and the report joined on `line_id`. */
export function consolidationRequest(
  lines: readonly ConsolidationLineSummary[],
  report: ReadonlyMap<string, ConsolidationReportRow> = new Map(),
): ExportRequest {
  const rows: ExportCell[][] = lines.map((line) => [
    { kind: 'text', value: line.line_id },
    { kind: 'text', value: line.source_store_key },
    { kind: 'text', value: line.destination_store_key },
    // The size as the platform named it, the key kept as an identifier (ADR 9027).
    ...sizeExportCells(sizeName(line)),
    { kind: 'integer', raw: line.units },
    // The ship-week a capped line is released in (bata-reqs ADR 9000); empty: ships as before.
    trancheWeekExportCell(trancheWeekOf(line)),
    moneyCell(parseDecimal(line.lane_cost_amount), line.currency_code),
    moneyCell(parseDecimal(line.incremental_revenue_amount), line.currency_code),
    moneyCell(parseDecimal(line.net_benefit_amount), line.currency_code),
    // The two bases the lane table shows beside the money (`docs/adr/9008`): a CSV that
    // carried the revenue without saying what it was estimated from would be the table
    // with its footnotes cut off.
    codeCell(line.revenue_basis),
    codeCell(line.elasticity_basis),
    codeCell(line.reason_code),
    flagCell(line.is_proposed, 'proposed', 'withheld'),
    ...consolidationAttributeCells(report.get(line.line_id)),
  ]);
  return {
    id: 'exit.consolidation.table',
    titleKey: 'exit.consolidation.title',
    columns: [
      { key: 'line_id', headerKey: 'exit.column.lineId', descriptionKey: 'exit.columnGuide.lane.lineId', kind: 'text' },
      { key: 'source_store_id', headerKey: 'exit.column.sourceDoor', descriptionKey: 'exit.columnGuide.lane.sourceDoor', kind: 'text' },
      { key: 'destination_store_id', headerKey: 'exit.column.destinationDoor', descriptionKey: 'exit.columnGuide.lane.destinationDoor', kind: 'text' },
      ...SIZE_EXPORT_COLUMNS,
      { key: 'units', headerKey: 'exit.column.units', descriptionKey: 'exit.columnGuide.lane.units', kind: 'integer' },
      TRANCHE_WEEK_EXPORT_COLUMN,
      { key: 'lane_cost_amount', headerKey: 'exit.column.laneCost', descriptionKey: 'exit.columnGuide.lane.laneCost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
      { key: 'incremental_revenue_amount', headerKey: 'exit.column.incrementalRevenue', descriptionKey: 'exit.columnGuide.lane.incrementalRevenue', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'net_benefit_amount', headerKey: 'exit.column.netBenefit', descriptionKey: 'exit.columnGuide.lane.netBenefit', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'revenue_basis', headerKey: 'exit.column.revenueBasis', descriptionKey: 'exit.columnGuide.lane.revenueBasis', kind: 'label', display: exportDisplayOf(REVENUE_BASIS_WORDS) },
      { key: 'elasticity_basis', headerKey: 'exit.column.elasticityBasis', descriptionKey: 'exit.columnGuide.lane.elasticityBasis', kind: 'label', display: exportDisplayOf(ELASTICITY_BASIS_WORDS) },
      { key: 'reason_code', headerKey: 'exit.column.reasonCode', descriptionKey: 'exit.columnGuide.lane.reasonCode', kind: 'label', display: exportDisplay(reasonLabelKey) },
      { key: 'state', headerKey: 'exit.column.state', descriptionKey: 'exit.columnGuide.lane.state', kind: 'label', display: exportDisplayOf(LANE_STATE_WORDS) },
      ...CONSOLIDATION_ATTRIBUTE_COLUMNS,
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([LANE_COST_DEFINITION], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(lines.map((line) => line.country_key))),
        ...observedPerimeter('as_of', distinct(lines.map((line) => line.as_of))),
      ],
      timeWindow: observedWindow(lines.map((line) => line.as_of)),
      markets: distinct(lines.map((line) => line.country_key)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'line_id', direction: 'asc' }],
      visibleColumns: ['line_id', 'source_store_id', 'destination_store_id', ...SIZE_EXPORT_KEYS, 'units', 'tranche_week', 'lane_cost_amount', 'incremental_revenue_amount', 'net_benefit_amount', 'revenue_basis', 'elasticity_basis', 'reason_code', 'state'],
      drillPath: ['option', 'door'],
      densityMode: 'compact',
    },
  };
}

/**
 * The option's doors, as the consolidation door table draws them — the CSV under that table
 * (`docs/adr/9008`). The route registers one export, the lane book; this is the other long
 * table on the same screen, and a download under it that handed over lanes would be the
 * wrong table.
 *
 * **An unranked door leaves as `missing`, never `0`**, and an unassigned cluster as
 * `missing`, never `''` — both for the reasons the spec states on the row itself.
 */
export function consolidationDoorsRequest(doors: readonly ConsolidationDoorSummary[]): ExportRequest {
  const rows: ExportCell[][] = doors.map((door) => [
    { kind: 'text', value: door.option_id },
    { kind: 'text', value: door.store_key },
    codeCell(door.cluster_id),
    codeCell(door.node_role),
    { kind: 'integer', raw: door.residual_units },
    { kind: 'integer', raw: door.sizes_in_stock },
    { kind: 'integer', raw: door.longest_contiguous_run },
    numberCell(parseDecimal(door.sell_through_per_capacity ?? null)),
    flagCell(door.run_is_broken, 'run_broken', 'run_complete'),
  ]);
  return {
    id: 'exit.consolidation.doors',
    titleKey: 'exit.consolidation.doorsTitle',
    columns: [
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'store_key', headerKey: 'exit.column.door', descriptionKey: 'exit.columnGuide.door.storeKey', kind: 'text' },
      { key: 'cluster_id', headerKey: 'exit.column.cluster', descriptionKey: 'exit.columnGuide.door.clusterId', kind: 'label' },
      { key: 'node_role', headerKey: 'exit.column.nodeRole', descriptionKey: 'exit.columnGuide.door.nodeRole', kind: 'label', display: exportDisplay(nodeRoleLabelKey) },
      { key: 'residual_units', headerKey: 'exit.column.residualUnits', descriptionKey: 'exit.columnGuide.door.residualUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'sizes_in_stock', headerKey: 'exit.column.sizesInStock', descriptionKey: 'exit.columnGuide.door.sizesInStock', kind: 'integer' },
      { key: 'longest_contiguous_run', headerKey: 'exit.column.longestRun', descriptionKey: 'exit.columnGuide.door.longestRun', kind: 'integer', polarity: 'higher-is-better' },
      { key: 'sell_through_per_capacity', headerKey: 'exit.column.sellThroughPerCapacity', descriptionKey: 'exit.columnGuide.door.sellThroughPerCapacity', kind: 'number', polarity: 'higher-is-better' },
      { key: 'state', headerKey: 'exit.column.state', descriptionKey: 'exit.columnGuide.door.state', kind: 'label', display: exportDisplayOf(DOOR_STATE_WORDS) },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([SELL_THROUGH_DEFINITION], {
      filters: [
        ...observedPerimeter('option_id', distinct(doors.map((door) => door.option_id))),
        ...observedPerimeter('run_id', distinct(doors.map((door) => door.run_id))),
        ...observedPerimeter('country_key', distinct(doors.map((door) => door.country_key))),
      ],
      timeWindow: observedWindow(doors.map((door) => door.as_of)),
      markets: distinct(doors.map((door) => door.country_key)),
    }),
    perimeter: {
      filters: [],
      // The order the read delivered and the table draws; the table applies no sort.
      sort: [],
      visibleColumns: ['option_id', 'store_key', 'cluster_id', 'node_role', 'residual_units', 'sizes_in_stock', 'longest_contiguous_run', 'sell_through_per_capacity', 'state'],
      drillPath: ['option', 'door'],
      densityMode: 'compact',
    },
  };
}

/**
 * The option directory — every option MATCHING the filter, in the order the directory table
 * draws them (largest residual first), of which the table draws fifty (`docs/adr/9008`).
 */
export function consolidationOptionsRequest(
  options: readonly ConsolidationOptionSummary[], filter: string,
): ExportRequest {
  const rows: ExportCell[][] = options.map((option) => [
    { kind: 'text', value: option.option_id },
    { kind: 'text', value: option.country_key },
    { kind: 'date', iso: option.as_of },
    { kind: 'integer', raw: option.doors },
    { kind: 'integer', raw: option.residual_units },
    { kind: 'integer', raw: option.doors_with_broken_run },
    { kind: 'integer', raw: option.doors_as_source },
    { kind: 'integer', raw: option.doors_as_destination },
  ]);
  const needle = filter.trim();
  const applied: readonly ProvenanceFilter[] = needle === ''
    ? []
    : [{ field: 'option_id', operator: 'contains', value: needle }];
  return {
    id: 'exit.consolidation.options',
    titleKey: 'exit.consolidation.optionsTitle',
    columns: [
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'as_of', headerKey: 'exit.column.asOfDate', descriptionKey: 'exit.columnGuide.option.asOf', kind: 'date' },
      { key: 'doors', headerKey: 'exit.column.stockingDoors', descriptionKey: 'exit.columnGuide.option.doors', kind: 'integer' },
      { key: 'residual_units', headerKey: 'exit.column.residualUnits', descriptionKey: 'exit.columnGuide.option.residualUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'doors_with_broken_run', headerKey: 'exit.measure.doorsWithBrokenRun', descriptionKey: 'exit.columnGuide.option.doorsWithBrokenRun', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'doors_as_source', headerKey: 'exit.column.doorsAsSource', descriptionKey: 'exit.columnGuide.option.doorsAsSource', kind: 'integer' },
      { key: 'doors_as_destination', headerKey: 'exit.measure.doorsAsDestination', descriptionKey: 'exit.columnGuide.option.doorsAsDestination', kind: 'integer' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.consolidation_option_position',
      definition: 'One option\'s consolidation position at one date, rolled up over its doors. Every figure is a count or a sum of the door rows for the same option and date; nothing is modelled. Doors with a broken run is a count of doors, not a share.',
    }], {
      filters: [
        ...applied,
        ...observedPerimeter('country_key', distinct(options.map((option) => option.country_key))),
      ],
      timeWindow: observedWindow(options.map((option) => option.as_of)),
    }),
    perimeter: {
      filters: applied,
      sort: [{ key: 'residual_units', direction: 'desc' }, { key: 'option_id', direction: 'asc' }],
      visibleColumns: ['option_id', 'country_key', 'as_of', 'doors', 'residual_units', 'doors_with_broken_run'],
      drillPath: ['option'],
      densityMode: 'compact',
    },
  };
}

// ── X6 — the floor-set go/no-go ──────────────────────────────────────────────

/** X6 — the readiness book, exported as the artefact the floor-set meeting reads. */
export async function handoverExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return handoverRequest(await collectPages(listHandoverReadiness, { query: marketQuery(scope) }), scope);
}

/** The readiness book's sheet over rows already read (`docs/adr/9008`). */
/**
 * The sheet with the market it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function handoverRequest(readiness: readonly HandoverReadinessSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(handoverSheet(readiness), scope, false);
}

function handoverSheet(readiness: readonly HandoverReadinessSummary[]): ExportRequest {
  const rows: ExportCell[][] = readiness.map((row) => [
    { kind: 'text', value: row.exit_plan_id },
    { kind: 'text', value: row.cluster_id },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.country_key },
    { kind: 'date', iso: row.floor_set_date },
    { kind: 'integer', raw: row.threshold_units },
    { kind: 'integer', raw: row.projected_units_at_floor_set },
    { kind: 'integer', raw: row.alert_lead_weeks },
    // Null where nothing is projected to breach. Not nought weeks out, which would read
    // as a breach happening this week.
    integerCell(row.first_breach_weeks_out),
    codeCell(row.state),
  ]);
  return {
    id: 'exit.handover.table',
    titleKey: 'exit.handover.title',
    columns: [
      { key: 'exit_plan_id', headerKey: 'exit.column.exitPlanId', descriptionKey: 'exit.columnGuide.common.exitPlanId', kind: 'text' },
      { key: 'cluster_id', headerKey: 'exit.column.cluster', descriptionKey: 'exit.columnGuide.handover.clusterId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'floor_set_date', headerKey: 'exit.column.floorSetDate', descriptionKey: 'exit.columnGuide.handover.floorSetDate', kind: 'date' },
      { key: 'threshold_units', headerKey: 'exit.column.thresholdUnits', descriptionKey: 'exit.columnGuide.handover.thresholdUnits', kind: 'integer' },
      { key: 'projected_units', headerKey: 'exit.column.projectedUnits', descriptionKey: 'exit.columnGuide.handover.projectedUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'alert_lead_weeks', headerKey: 'exit.column.alertLeadWeeks', descriptionKey: 'exit.columnGuide.handover.alertLeadWeeks', kind: 'integer', polarity: 'higher-is-better' },
      { key: 'first_breach_weeks_out', headerKey: 'exit.column.firstBreachWeeksOut', descriptionKey: 'exit.columnGuide.handover.firstBreachWeeksOut', kind: 'integer', polarity: 'higher-is-better' },
      { key: 'state', headerKey: 'exit.column.state', descriptionKey: 'exit.columnGuide.handover.state', kind: 'label', display: exportDisplay(handoverStateLabelKey) },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.handover_readiness_gap',
      definition: 'Projected residual units at the floor-set date less the cluster space threshold, back-planned from the incoming floor set. The alert lead is the weeks between the alert and the projected breach; reqs/05 §8 criterion 8 requires at least four.',
    }], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(readiness.map((row) => row.country_key))),
        ...observedPerimeter('cluster_id', distinct(readiness.map((row) => row.cluster_id))),
      ],
      timeWindow: observedWindow(readiness.map((row) => row.floor_set_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'alert_lead_weeks', direction: 'asc' }],
      visibleColumns: ['exit_plan_id', 'cluster_id', 'option_id', 'country_key', 'floor_set_date', 'threshold_units', 'projected_units', 'alert_lead_weeks', 'first_breach_weeks_out', 'state'],
      drillPath: ['cluster'],
      densityMode: 'compact',
    },
  };
}

// ── X4 and X5 — exit-channel routing ─────────────────────────────────────────

/**
 * X4 and X5 — every route considered, including the losers and the unscored.
 *
 * The export is the whole comparison rather than the chosen row, because reqs/05 §8
 * criterion 10 requires net realisation shown per route and a chosen-row-only extract
 * makes the decision unauditable outside the product.
 *
 * `permitted`, `was_scored` and the reason stay three columns. A route the brand rules
 * excluded, a permitted route nobody could price and a permitted route that lost on price
 * are three findings, and a sheet that collapsed them would send stock down the wrong
 * channel at the first negotiation it was quoted in.
 */
export async function routingExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return routingRequest(await collectPages(listRouting, { query: marketQuery(scope) }), scope);
}

/** Every lot's comparison over decisions already read (`docs/adr/9008`). */
/**
 * The sheet with the market it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function routingRequest(decisions: readonly RoutingSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(routingSheet(decisions), scope, false);
}

function routingSheet(decisions: readonly RoutingSummary[]): ExportRequest {
  const rows: ExportCell[][] = decisions.flatMap((lot) =>
    (lot.considered ?? []).map((route): ExportCell[] => [
      { kind: 'text', value: lot.routing_decision_id },
      { kind: 'text', value: lot.lot_key },
      { kind: 'text', value: lot.option_id },
      { kind: 'text', value: lot.country_key },
      { kind: 'integer', raw: lot.units },
      codeCell(route.route_code),
      flagCell(route.permitted !== false, 'permitted', 'gated_out'),
      flagCell(route.was_scored !== false, 'scored', 'not_scored'),
      moneyCell(parseDecimal(route.gross_realisation), lot.currency_code),
      moneyCell(parseDecimal(route.freight_cost), lot.currency_code),
      moneyCell(parseDecimal(route.handling_cost), lot.currency_code),
      moneyCell(parseDecimal(route.reticketing_cost), lot.currency_code),
      moneyCell(parseDecimal(route.holding_cost), lot.currency_code),
      // ADR 0061 at the boundary: an unmeasured route is `missing`, never zero.
      moneyCell(parseDecimal(route.net_realisation), lot.currency_code),
      moneyCell(parseDecimal(route.net_realisation_per_unit), lot.currency_code),
      codeCell(route.unavailable_reason_code),
      codeCell(route.realisation_basis),
      flagCell(route.route_code === lot.chosen_route_code, 'chosen', 'considered'),
    ]),
  );
  return {
    id: 'exit.routes.table',
    titleKey: 'exit.routes.title',
    columns: [
      { key: 'routing_decision_id', headerKey: 'exit.column.routingDecisionId', descriptionKey: 'exit.columnGuide.route.routingDecisionId', kind: 'text' },
      { key: 'lot_key', headerKey: 'exit.column.lotId', descriptionKey: 'exit.columnGuide.route.lotKey', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'units', headerKey: 'exit.column.units', descriptionKey: 'exit.columnGuide.route.units', kind: 'integer' },
      { key: 'route_code', headerKey: 'exit.column.route', descriptionKey: 'exit.columnGuide.route.routeCode', kind: 'label', display: exportDisplay(routeLabelKey) },
      { key: 'permission', headerKey: 'exit.column.permission', descriptionKey: 'exit.columnGuide.route.permission', kind: 'label' },
      { key: 'was_scored', headerKey: 'exit.column.wasScored', descriptionKey: 'exit.columnGuide.route.wasScored', kind: 'label' },
      { key: 'gross_realisation', headerKey: 'exit.column.grossRealisation', descriptionKey: 'exit.columnGuide.route.grossRealisation', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'freight_cost', headerKey: 'exit.column.freightCost', descriptionKey: 'exit.columnGuide.route.freightCost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
      { key: 'handling_cost', headerKey: 'exit.column.handlingCost', descriptionKey: 'exit.columnGuide.route.handlingCost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
      { key: 'reticketing_cost', headerKey: 'exit.column.reticketingCost', descriptionKey: 'exit.columnGuide.route.reticketingCost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
      { key: 'holding_cost', headerKey: 'exit.column.holdingCost', descriptionKey: 'exit.columnGuide.route.holdingCost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
      { key: 'net_realisation', headerKey: 'exit.column.netRealisation', descriptionKey: 'exit.columnGuide.route.netRealisation', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'net_realisation_per_unit', headerKey: 'exit.column.netRealisationPerUnit', descriptionKey: 'exit.columnGuide.route.netRealisationPerUnit', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
      { key: 'unavailable_reason', headerKey: 'exit.column.unavailableReason', descriptionKey: 'exit.columnGuide.route.unavailableReason', kind: 'label', display: exportDisplay(routeUnavailableLabelKey) },
      { key: 'realisation_basis', headerKey: 'exit.column.realisationBasis', descriptionKey: 'exit.columnGuide.route.realisationBasis', kind: 'label', display: exportDisplay(realisationBasisLabelKey) },
      { key: 'outcome', headerKey: 'exit.column.outcome', descriptionKey: 'exit.columnGuide.route.outcome', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.route_realisation_rate',
      definition: 'Net realisation per unit for an exit route: gross realisation less freight, handling, re-ticketing and holding over the route lead time. Undefined where the route carries neither fitted history nor an authored parameter; the row carries the reason where the service published one.',
    }], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(decisions.map((lot) => lot.country_key))),
        ...observedPerimeter('scenario_id', distinct(decisions.map((lot) => lot.scenario_id))),
      ],
      // The routing decision carries no date at all, so the sheet states an empty window
      // rather than the day it was downloaded. A window is a claim about the data.
      timeWindow: { from: '', to: '' },
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'lot_key', direction: 'asc' }, { key: 'net_realisation', direction: 'desc' }],
      visibleColumns: ['routing_decision_id', 'lot_key', 'option_id', 'country_key', 'units', 'route_code', 'permission', 'was_scored', 'gross_realisation', 'freight_cost', 'handling_cost', 'reticketing_cost', 'holding_cost', 'net_realisation', 'net_realisation_per_unit', 'unavailable_reason', 'realisation_basis', 'outcome'],
      drillPath: ['article'],
      densityMode: 'compact',
    },
  };
}

// ── X7 — returns ─────────────────────────────────────────────────────────────

/** X7 — the disposition log, with the segregation flag on every row. */
export async function returnsExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  // Both sides of the segregation flag, as the screen reads them (DEC-111 follow-ups).
  return returnsRequest(await collectPages(listReturns, { query: { ...marketQuery(scope), segregation: RETURNS_SEGREGATION } }), scope);
}

/** The disposition log's sheet over rows already read (`docs/adr/9008`). */
/**
 * The sheet with the market it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function returnsRequest(returns: readonly ReturnDispositionSummary[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(returnsSheet(returns), scope, false);
}

function returnsSheet(returns: readonly ReturnDispositionSummary[]): ExportRequest {
  const rows: ExportCell[][] = returns.map((row) => [
    { kind: 'text', value: row.return_id },
    { kind: 'text', value: row.option_id },
    ...sizeExportCells(sizeName(row)),
    codeCell(row.disposition),
    { kind: 'integer', raw: row.units },
    numberCell(parseDecimal(row.realisation_per_unit)),
    numberCell(parseDecimal(row.net_value)),
    codeCell(row.route_code),
    codeCell(row.resulting_grade_code),
    flagCell(row.counts_toward_full_price, 'eligible', 'segregated'),
  ]);
  return {
    id: 'exit.returns.table',
    titleKey: 'exit.returns.title',
    columns: [
      { key: 'return_id', headerKey: 'exit.column.returnId', descriptionKey: 'exit.columnGuide.returns.returnId', kind: 'text' },
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      ...SIZE_EXPORT_COLUMNS,
      { key: 'disposition', headerKey: 'exit.column.disposition', descriptionKey: 'exit.columnGuide.returns.disposition', kind: 'label', display: exportDisplay(returnDispositionLabelKey) },
      { key: 'units', headerKey: 'exit.column.units', descriptionKey: 'exit.columnGuide.returns.units', kind: 'integer' },
      { key: 'realisation_per_unit', headerKey: 'exit.column.realisationPerUnit', descriptionKey: 'exit.columnGuide.returns.realisationPerUnit', kind: 'number', polarity: 'higher-is-better' },
      { key: 'net_value', headerKey: 'exit.column.netValue', descriptionKey: 'exit.columnGuide.returns.netValue', kind: 'number', polarity: 'higher-is-better' },
      { key: 'route_code', headerKey: 'exit.column.route', descriptionKey: 'exit.columnGuide.returns.routeCode', kind: 'label', display: exportDisplay(routeLabelKey) },
      { key: 'resulting_grade_code', headerKey: 'exit.column.resultingGrade', descriptionKey: 'exit.columnGuide.returns.resultingGrade', kind: 'label', display: exportDisplay(gradeLabelKey) },
      { key: 'full_price_availability', headerKey: 'exit.column.fullPriceAvailability', descriptionKey: 'exit.columnGuide.returns.fullPriceAvailability', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.returns_disposition_share',
      definition: 'Share of returned units routed to each disposition by condition grade. Refurbished units carry a distinct grade and are excluded from full-price availability. Realisation per unit is undefined — never zero — where the refurbishment cost input has not landed.',
    }], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('resulting_grade_code', distinct(returns.map((row) => row.resulting_grade_code))),
      ],
      // The disposition row carries no date. Stated empty rather than invented.
      timeWindow: { from: '', to: '' },
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'return_id', direction: 'asc' }],
      visibleColumns: ['return_id', 'option_id', ...SIZE_EXPORT_KEYS, 'disposition', 'units', 'realisation_per_unit', 'net_value', 'route_code', 'resulting_grade_code', 'full_price_availability'],
      drillPath: ['store'],
      densityMode: 'compact',
    },
  };
}

// ── the estate position, and the join ────────────────────────────────────────

/**
 * The landing surface's sheet — the stranded-lot join, exported with its evidence.
 *
 * Three reads and a predicate, and the export runs the same three. `at_price_floor` and
 * `route_scorable` leave as `missing` where the fact could not be established, because
 * this is the sheet somebody will filter on `FALSE` to find the safe options — and an
 * option nobody could judge is not a safe option. `factCell` is the type-level version of
 * that sentence.
 */
export async function positionExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  const [plans, proposals, decisions] = await Promise.all([
    collectPages(exitListPlans, { query: marketSeasonQuery(scope) }),
    collectPages(listCadenceProposals, { query: marketQuery(scope) }),
    collectPages(listRouting, { query: marketQuery(scope) }),
  ]);
  // A market and season's plans, not one run's output: the sheet says so (bata-reqs ADR 9001).
  return withParametersInForce(positionRequest(orderStranded(strandedLots(plans, proposals, decisions)), plans, scope), NO_RUN);
}

/**
 * The stranded join's sheet over lots already joined, in the order the table draws them —
 * stranded, then undetermined, then the rest (`orderStranded`). The route's export used to
 * leave in join order while its perimeter claimed a sort; both paths now apply the one the
 * screen applies (`docs/adr/9008`). With the market and season its join was read under
 * (usability audit C9).
 */
export function positionRequest(
  lots: readonly StrandedLot[], plans: readonly ExitPlanSummary[], scope: MarketSeason = exportMarketSeason(),
): ExportRequest {
  return withMarketSeason(positionSheet(lots, plans), scope, true);
}

function positionSheet(
  lots: readonly StrandedLot[], plans: readonly ExitPlanSummary[],
): ExportRequest {
  const rows: ExportCell[][] = lots.map((lot) => [
    { kind: 'text', value: lot.optionId },
    { kind: 'text', value: lot.countryKey },
    factCell(lot.atFloor, 'at_price_floor', 'above_floor'),
    factCell(lot.noScorableRoute, 'no_route_scorable', 'route_scorable'),
    integerCell(lot.weeksToHandover),
    moneyCell(lot.priceFloorAmount, lot.currencyCode),
    { kind: 'label', value: lot.verdict, code: lot.verdict },
  ]);
  return {
    // The Stranded page's sheet since the position screen was split (`docs/adr/0068`,
    // amendment of 2026-09-30); it was the position route's.
    id: 'exit.stranded.table',
    titleKey: 'exit.stranded.title',
    columns: [
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'country_key', headerKey: 'exit.column.countryKey', descriptionKey: 'exit.columnGuide.common.countryKey', kind: 'text' },
      { key: 'at_price_floor', headerKey: 'exit.column.atPriceFloor', descriptionKey: 'exit.columnGuide.stranded.atPriceFloor', kind: 'label' },
      { key: 'route_scorable', headerKey: 'exit.column.routeScorable', descriptionKey: 'exit.columnGuide.stranded.routeScorable', kind: 'label' },
      { key: 'weeks_to_handover', headerKey: 'exit.column.weeksToHandover', descriptionKey: 'exit.columnGuide.plan.weeksToHandover', kind: 'integer', polarity: 'higher-is-better' },
      { key: 'price_floor_amount', headerKey: 'exit.column.priceFloor', descriptionKey: 'exit.columnGuide.plan.priceFloor', kind: 'money', currency: 'COP' },
      { key: 'verdict', headerKey: 'exit.column.verdict', descriptionKey: 'exit.columnGuide.stranded.verdict', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.stranded_lot',
      definition: 'An option at its price floor, with no permitted exit route the service could score, inside four weeks of its handover date. Joined in the front end from the exit plan book, the cadence queue and the routing decisions; each state is published separately and the join is published nowhere. A row missing any of the three facts is undetermined and is never reported as not stranded.',
    }, TSR_DEFINITION], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(plans.map((row) => row.country_key))),
        { field: 'handover_horizon_weeks', operator: 'equals', value: '4' },
      ],
      timeWindow: observedWindow(plans.map((row) => row.handover_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'verdict', direction: 'asc' }, { key: 'weeks_to_handover', direction: 'asc' }],
      visibleColumns: ['option_id', 'country_key', 'at_price_floor', 'route_scorable', 'weeks_to_handover', 'price_floor_amount', 'verdict'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The Position page's sheet — terminal stock by lifecycle class, one row per class, as the
 * table draws it (`docs/adr/0060`: the worst PUBLISHED ratio against that option's own
 * target, never an average this repository computed).
 */
export async function lifecycleExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  const plans = await collectPages(exitListPlans, { query: marketSeasonQuery(scope) });
  return withParametersInForce(lifecycleRequest(lifecycleStandings(plans), plans, scope), NO_RUN);
}

/** The lifecycle sheet over standings already derived, in the table's order. Pure. */
/** The lifecycle sheet with the market and season its plans were read under (usability audit C9). */
export function lifecycleRequest(
  standings: readonly LifecycleStanding[], plans: readonly ExitPlanSummary[], scope: MarketSeason = exportMarketSeason(),
): ExportRequest {
  return withMarketSeason(lifecycleSheet(standings, plans), scope, true);
}

function lifecycleSheet(
  standings: readonly LifecycleStanding[], plans: readonly ExitPlanSummary[],
): ExportRequest {
  const rows: ExportCell[][] = standings.map((standing) => [
    { kind: 'label', value: standing.lifecycleClass, code: standing.lifecycleClass },
    { kind: 'integer', raw: standing.options },
    standing.worstOptionId === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: standing.worstOptionId },
    numberCell(standing.worstRatio),
    numberCell(standing.worstTarget),
    { kind: 'integer', raw: standing.aboveTarget },
    { kind: 'integer', raw: standing.ratioUnstated },
  ]);
  return {
    id: 'exit.position.lifecycle',
    titleKey: 'exit.position.lifecycleTitle',
    columns: [
      { key: 'lifecycle_class', headerKey: 'exit.column.lifecycleClass', descriptionKey: 'exit.columnGuide.plan.lifecycleClass', kind: 'label', display: exportDisplay(lifecycleLabelKey) },
      { key: 'options', headerKey: 'exit.column.optionsInClass', descriptionKey: 'exit.columnGuide.position.options', kind: 'integer' },
      { key: 'worst_option', headerKey: 'exit.column.worstOption', descriptionKey: 'exit.columnGuide.position.worstOption', kind: 'text' },
      { key: 'worst_ratio', headerKey: 'exit.column.terminalStockRatio', descriptionKey: 'exit.columnGuide.position.worstRatio', kind: 'number', polarity: 'lower-is-better' },
      { key: 'worst_target', headerKey: 'exit.column.targetRatio', descriptionKey: 'exit.columnGuide.position.worstTarget', kind: 'number' },
      { key: 'above_target', headerKey: 'exit.column.optionsAboveTarget', descriptionKey: 'exit.columnGuide.position.aboveTarget', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'ratio_unstated', headerKey: 'exit.column.ratiosUnstated', descriptionKey: 'exit.columnGuide.position.ratioUnstated', kind: 'integer' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([TSR_DEFINITION], {
      filters: [
        ...NOT_APPLIED,
        ...observedPerimeter('country_key', distinct(plans.map((row) => row.country_key))),
      ],
      timeWindow: observedWindow(plans.map((row) => row.handover_date)),
    }),
    perimeter: {
      filters: NOT_APPLIED,
      sort: [{ key: 'lifecycle_class', direction: 'asc' }],
      visibleColumns: ['lifecycle_class', 'options', 'worst_option', 'worst_ratio', 'worst_target', 'above_target', 'ratio_unstated'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

// ── gate D4 ──────────────────────────────────────────────────────────────────

/**
 * Gate D4's evidence.
 *
 * **`realised_price_delta` leaves as `missing` whenever the row is not evaluable**, and
 * that is the whole point of the handler. A CSV in which an inadmissible gate row and a
 * genuine zero improvement both read `0` has destroyed the distinction the D4 decision
 * rests on — and unlike a screen, a CSV is quoted months later by someone who never saw
 * the state the screen was in.
 */
export async function evidenceExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = exportMarketSeason(given);
  return evidenceRequest(await collectPages(listD4Evidence, { query: marketSeasonQuery(scope) }), scope);
}

/** Gate D4's sheet over rows already read (`docs/adr/9008`). */
/**
 * The sheet with the market and season it was read under (usability audit C9) — the address bar's
 * unless the caller states one.
 */
export function evidenceRequest(evidence: readonly D4EvidenceRow[], scope: MarketSeason = exportMarketSeason()): ExportRequest {
  return withMarketSeason(evidenceSheet(evidence), scope, true);
}

function evidenceSheet(evidence: readonly D4EvidenceRow[]): ExportRequest {
  const rows: ExportCell[][] = evidence.map((row) => [
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.cluster_id },
    { kind: 'text', value: row.season_code },
    numberCell(parseDecimal(row.shadow_realised_price)),
    numberCell(parseDecimal(row.incumbent_realised_price)),
    // Never `?? 0`. See the note above.
    numberCell(d4Delta(row)),
    integerCell(row.shadow_terminal_units),
    integerCell(row.incumbent_terminal_units),
    { kind: 'label', value: d4Verdict(row), code: d4Verdict(row) },
    { kind: 'text', value: d4ReasonCodes(row).join(',') },
  ]);
  return {
    id: 'exit.evidence.table',
    titleKey: 'exit.evidence.title',
    columns: [
      { key: 'option_id', headerKey: 'exit.column.option', descriptionKey: 'exit.columnGuide.common.optionId', kind: 'text' },
      { key: 'cluster_id', headerKey: 'exit.column.cluster', descriptionKey: 'exit.columnGuide.d4.clusterId', kind: 'text' },
      { key: 'season_code', headerKey: 'exit.column.seasonCode', descriptionKey: 'exit.columnGuide.d4.seasonCode', kind: 'text' },
      { key: 'shadow_realised_price', headerKey: 'exit.column.shadowRealisedPrice', descriptionKey: 'exit.columnGuide.d4.shadowRealisedPrice', kind: 'number', polarity: 'higher-is-better' },
      { key: 'incumbent_realised_price', headerKey: 'exit.column.incumbentRealisedPrice', descriptionKey: 'exit.columnGuide.d4.incumbentRealisedPrice', kind: 'number', polarity: 'higher-is-better' },
      { key: 'realised_price_delta', headerKey: 'exit.column.realisedPriceDelta', descriptionKey: 'exit.columnGuide.d4.realisedPriceDelta', kind: 'number', polarity: 'higher-is-better' },
      { key: 'shadow_terminal_units', headerKey: 'exit.column.shadowTerminalUnits', descriptionKey: 'exit.columnGuide.d4.shadowTerminalUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'incumbent_terminal_units', headerKey: 'exit.column.incumbentTerminalUnits', descriptionKey: 'exit.columnGuide.d4.incumbentTerminalUnits', kind: 'integer', polarity: 'lower-is-better' },
      { key: 'verdict', headerKey: 'exit.column.verdict', descriptionKey: 'exit.columnGuide.d4.verdict', kind: 'label' },
      { key: 'not_evaluable_reason_codes', headerKey: 'exit.column.notEvaluableBecause', descriptionKey: 'exit.columnGuide.d4.notEvaluableReasons', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{
      measure: 'exit.d4_realised_price_delta',
      definition: 'Shadow-cadence realised price less incumbent realised price, at constant terminal stock, per option and cluster over a complete shadow season. Undefined — never zero — while the comparison is not evaluable; the row carries the reason codes that make it so.',
    }], {
      filters: [
        { field: 'gate', operator: 'equals', value: 'D4' },
        ...observedPerimeter('season_code', distinct(evidence.map((row) => row.season_code))),
      ],
      // The evidence row is stored per season and carries no date range of its own.
      timeWindow: { from: '', to: '' },
    }),
    perimeter: {
      filters: [{ field: 'gate', operator: 'equals', value: 'D4' }],
      sort: [{ key: 'option_id', direction: 'asc' }],
      visibleColumns: ['option_id', 'cluster_id', 'season_code', 'shadow_realised_price', 'incumbent_realised_price', 'realised_price_delta', 'shadow_terminal_units', 'incumbent_terminal_units', 'verdict', 'not_evaluable_reason_codes'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * `/exit/consolidation/:storeKey` — the lanes touching one door in the run, both directions
 * (bata-ui#148 4.4, ADR 7002). Two bounded reads with the platform's own door filters, never
 * the run's whole book filtered here; no run, no read.
 */
export async function doorLanesExport(): Promise<ExportRequest> {
  const scope = readExitScope(typeof window === 'undefined' ? '' : window.location.search);
  const storeKey = readDoorDrill(typeof window === 'undefined' ? '' : window.location.pathname);
  if (scope.consolidationRunId === null || storeKey === null) return withParametersInForce(consolidationRequest([]), NO_RUN);
  const [outgoing, incoming] = await Promise.all([
    collectPages(listConsolidationLines, { query: { run_id: scope.consolidationRunId, source_store_key: storeKey } }),
    collectPages(listConsolidationLines, { query: { run_id: scope.consolidationRunId, destination_store_key: storeKey } }),
  ]);
  const seen = new Set<string>();
  const lines = [...outgoing, ...incoming].filter((line) => (seen.has(line.line_id) ? false : (seen.add(line.line_id), true)));
  // Its own title, so a door's file is never named as the run's lane book (`docs/adr/9048`); the run
  // and the door (either end of a lane) stated as the two reads asked for them.
  return withRunParameters(statingFilters(
    { ...(await consolidationReportRequest(lines)), id: 'exit.consolidation.door', titleKey: 'exit.consolidation.doorLanesTitle' },
    [
      { field: 'run_id', operator: 'equals', value: scope.consolidationRunId },
      { field: 'store_key', operator: 'equals', value: storeKey },
    ],
  ), [scope.consolidationRunId]);
}
